import { ArrowLeft } from "lucide-react";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { PayoutsVerificationGate } from "@/modules/verification/components/PayoutsVerificationGate";
import { useOrganizationVerificationStatus } from "@/modules/verification/hooks/useOrganizationVerificationStatus";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaLineSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaPageHeader } from "@/shared/components/layout/ColocaPageHeader";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import { NoAccessView } from "@/shared/components/NoAccessView";
import { TwoFactorActionModals } from "@/shared/components/overlays/TwoFactorActionModals";
import useTranslations from "@/shared/lang/useTranslations";

import { BatchStepper } from "../components/detail/BatchStepper";
import { BatchGuideButton } from "../components/guide/BatchGuideButton";
import { BatchSetupForm } from "../components/setup/BatchSetupForm";
import { useUploadBatch } from "../hooks/useUploadBatch";
import { batchPath, newBatchPath, PAYOUT_BATCHES_PATH } from "../utils/batchLinks";
import { canWriteBatches } from "../utils/batchPermissions";
import { type BatchSetupValues, splitCorridor } from "../utils/batchSetupSchema";

/**
 * `/app/payout-batches/new` — step 1 of the wizard. The upload is the first step-up action: it goes out with
 * the TOTP the person types, and a 201 lands them on the batch, which validates asynchronously. Members whose
 * organization cannot send payouts yet see the verification gate instead, as in the dispersions wizard.
 */
const PayoutBatchCreatePage = () => {
  const { t } = useTranslations("payoutBatches");
  const { t: tGlobal } = useTranslations("global");
  const navigate = useNavigate();
  const { isInternalUser, user } = useAuth();
  const verification = useOrganizationVerificationStatus();
  const { upload, isUploading, error, twoFactor } = useUploadBatch();

  const breadcrumb = useMemo(
    () => [
      { label: tGlobal("sidebar_section_billeteras") },
      { label: tGlobal("sidebar_menu_payout_batches"), to: PAYOUT_BATCHES_PATH },
      { label: t("action_new_batch"), to: newBatchPath() },
    ],
    [t, tGlobal],
  );
  useAppShellBreadcrumb(breadcrumb);

  const handleSubmit = useCallback(
    async (values: BatchSetupValues) => {
      const corridor = splitCorridor(values.corridor);
      if (!corridor) return;
      const batch = await upload({
        file: values.file,
        sourceCurrency: values.sourceCurrency,
        country: corridor.country,
        rail: corridor.rail,
        concept: values.concept || undefined,
        organizationId: isInternalUser ? values.organizationId || undefined : undefined,
      });
      if (batch) navigate(batchPath(batch.id), { replace: true });
    },
    [isInternalUser, navigate, upload],
  );

  // Uploading needs payout_batches:write; the backend would refuse the upload anyway.
  if (!canWriteBatches(user)) return <NoAccessView />;

  if (verification.isOrgMember && verification.isLoading && verification.data === null) {
    return (
      <div className="mx-auto w-full max-w-[720px] space-y-6 p-2">
        <ColocaLineSkeleton lines={4} />
      </div>
    );
  }

  if (verification.isOrgMember && !verification.canCreatePayouts) {
    return (
      <div className="mx-auto w-full max-w-[720px] space-y-6 p-2">
        <PayoutsVerificationGate status={verification} />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-3xl space-y-6 p-2">
      <ColocaPageHeader
        title={t("setup_title")}
        description={t("setup_subtitle")}
        actions={
          <>
            <BatchGuideButton />
            <ColocaButton variant="ghost" icon={ArrowLeft} onClick={() => navigate(PAYOUT_BATCHES_PATH)}>
              {t("action_back_to_list")}
            </ColocaButton>
          </>
        }
      />

      <BatchStepper />

      <BatchSetupForm
        isInternal={!!isInternalUser}
        onSubmit={handleSubmit}
        isSubmitting={isUploading}
        submitError={error}
      />

      <TwoFactorActionModals controller={twoFactor} requiredDescription={t("setup_two_factor_required")} />
    </div>
  );
};

export default PayoutBatchCreatePage;
